import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./landing.css";

const FA5_URL =
  "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.13.0/css/all.min.css";
const INTER_URL =
  "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap";

// Adds a <link> to <head> only while the landing page is mounted,
// so Font Awesome 5 / Inter don't affect the rest of the app.
function useStylesheet(href: string) {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, [href]);
}

export default function Landing() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useStylesheet(FA5_URL);
  useStylesheet(INTER_URL);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Wendwise - Your Insurance Research Analyst Sidekick";
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.title = prevTitle;
    };
  }, []);

  return (
    <div className="wendwise-landing">
      {/* NAVBAR */}
      <nav className={`navbar${scrolled ? " scrolled" : ""}`} id="navbar">
        <div className="container">
          <Link className="navbar-brand" to="/">
            Wendwise
          </Link>
          <div
            className={`hamburger${menuOpen ? " active" : ""}`}
            id="hamburger"
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span></span>
            <span></span>
            <span></span>
          </div>
          <ul className={`navbar-menu${menuOpen ? " active" : ""}`} id="navbarMenu">
            <li>
              <Link to="/login" className="nav-link" onClick={() => setMenuOpen(false)}>
                Login
              </Link>
            </li>
          </ul>
        </div>
      </nav>

      {/* HERO */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1>Wendwise is your Insurance Research Analyst Sidekick</h1>
              <p>
                Think "Expert Driven Insurance Wiki". Wendwise complements your research
                efforts, saving you and your company time and money.
              </p>
            </div>
            <div className="hero-image">
              <img
                src="/about/images/f533035f-9995-4738-af55-ba7c3c613acc.png"
                alt="Wendwise Dashboard"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="section-title">
            Would you like a little help collecting and managing your secondary research on
            important insurance companies, topics and trends?
          </h2>
          <p className="section-subtitle">
            We offer a unique and affordable solution that can help you manage this
            information more efficiently
          </p>
        </div>
      </section>

      {/* FEATURES */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">One Platform. Many Uses. Instant 5x Value.</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <img src="/about/icons/market-intel.png" alt="Market and company intelligence" />
              </div>
              <h3>Market/Company Intel</h3>
              <p>
                Access an easy to use, searchable database of industry topics, companies,
                solution providers, products/services and other useful insights on
                companies, curated by Wendwise.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <img src="/about/icons/partnerships.png" alt="Procurement and partnerships" />
              </div>
              <h3>Procurement &amp; Partnerships</h3>
              <p>
                Research and identify companies by insurance specific categories to form
                alliances with or procure solutions from.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <img src="/about/icons/product-development.png" alt="Product development" />
              </div>
              <h3>Product Development</h3>
              <p>
                Research what products and solutions are available to help understand the
                landscape for product enhancements and/or alliances.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VAULT */}
      <section className="section vault-section dark">
        <div className="container vault-grid">
          <div>
            <span className="new-badge">New Feature</span>
            <h2 className="section-title" style={{ textAlign: "left" }}>
              Personal Research Vault
              <br />
              <span style={{ fontWeight: 400 }}>Drag &amp; Drop Intelligence Capture</span>
            </h2>
            <p className="section-subtitle" style={{ textAlign: "left" }}>
              Stop losing valuable insurance research, insights and links across tabs,
              emails, and folders. Capture insights instantly and turn them into reusable
              intelligence.
            </p>
            <ul className="vault-points">
              <li>
                <i className="fas fa-check-circle"></i> Drag &amp; drop web-based content
                such as articles, links and pdfs - no browser extension needed
              </li>
              <li>
                <i className="fas fa-check-circle"></i> Add notes, summaries, and context
              </li>
              <li>
                <i className="fas fa-check-circle"></i> Search &amp; filter your research
                anytime
              </li>
              <li>
                <i className="fas fa-check-circle"></i> Export insights to Excel in one
                click
              </li>
              <li>
                <i className="fas fa-check-circle"></i> Collaborate with your teammates
                without losing ownership
              </li>
            </ul>
          </div>
          <div className="drag-card">
            <div className="drag-zone">
              <i className="fas fa-cloud-upload-alt"></i>
              <h4>Drop your research here</h4>
              <p>Articles • Reports • Links • Notes</p>
            </div>
            <div className="drag-hint">
              <span>
                <i className="fas fa-tags"></i> Tag
              </span>
              <span>
                <i className="fas fa-sticky-note"></i> Annotate
              </span>
              <span>
                <i className="fas fa-database"></i> Save
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT SECTIONS */}
      <section className="section">
        <div className="container">
          <div className="content-section">
            <div className="content-text">
              <h2>We act behind the scenes, your secret research assistant</h2>
              <p>
                Wendwise is a cost-effective unique solution - we are constantly updating
                our database with secondary research for the benefit of our subscribers.
                We can also curate content in Wendwise based on your unique interests and
                desired objectives
              </p>
              <hr />
            </div>
            <div className="content-image">
              <div style={{ width: 340, height: 340, margin: "0 auto" }}>
                <svg width="340" height="340" viewBox="0 0 280 280" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="lw-grad1" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#667eea" stopOpacity={1} />
                      <stop offset="100%" stopColor="#764ba2" stopOpacity={1} />
                    </linearGradient>
                    <linearGradient id="lw-grad2" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#ff7a18" stopOpacity={1} />
                      <stop offset="100%" stopColor="#ffb347" stopOpacity={1} />
                    </linearGradient>
                    <filter id="lw-glow">
                      <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                      <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>
                  <circle cx="140" cy="140" r="100" fill="url(#lw-grad1)" opacity="0.1" />
                  <line x1="180" y1="180" x2="220" y2="220" stroke="url(#lw-grad2)" strokeWidth="12" strokeLinecap="round" filter="url(#lw-glow)" />
                  <circle cx="140" cy="140" r="60" fill="none" stroke="url(#lw-grad1)" strokeWidth="8" filter="url(#lw-glow)" />
                  <circle cx="140" cy="140" r="35" fill="url(#lw-grad1)" opacity="0.3" />
                  <circle cx="140" cy="140" r="18" fill="url(#lw-grad2)" filter="url(#lw-glow)">
                    <animate attributeName="r" values="18;20;18" dur="2s" repeatCount="indefinite" />
                  </circle>
                  <circle cx="148" cy="132" r="6" fill="white" opacity="0.8" />
                  <g opacity="0.7">
                    <circle cx="100" cy="100" r="4" fill="#667eea">
                      <animate attributeName="opacity" values="0.3;1;0.3" dur="2s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="180" cy="100" r="4" fill="#ff7a18">
                      <animate attributeName="opacity" values="0.3;1;0.3" dur="2.5s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="100" cy="180" r="4" fill="#764ba2">
                      <animate attributeName="opacity" values="0.3;1;0.3" dur="3s" repeatCount="indefinite" />
                    </circle>
                  </g>
                </svg>
              </div>
            </div>
          </div>

          <div className="content-section reverse">
            <div className="content-text">
              <h2>How it works</h2>
              <p>
                Wendwise adds a majority of the data to the database collective - however,
                we encourage our subscribers to collaborate with Wendwise by contributing
                non-confidential secondary research or provide feedback. We can also curate
                research based on your unique needs - you tell us what companies, topics
                and trends you are interested in and we work together to collect and add
                that to Wendwise based on your requirements. Our unique model creates a
                continuously evolving and growing "insurance specific wiki", with Wendwise
                acting as an administrator. The result is a more powerful repository of
                insurance industry related research than a single company can amass
                themselves, at a price any enterprise can afford!
              </p>
              <hr />
            </div>
            <div className="content-image">
              <div style={{ width: 340, height: 340, margin: "0 auto" }}>
                <svg width="340" height="340" viewBox="0 0 280 280" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="lw-grad3" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#667eea" stopOpacity={1} />
                      <stop offset="100%" stopColor="#764ba2" stopOpacity={1} />
                    </linearGradient>
                    <linearGradient id="lw-grad4" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#06b6d4" stopOpacity={1} />
                      <stop offset="100%" stopColor="#8b5cf6" stopOpacity={1} />
                    </linearGradient>
                    <linearGradient id="lw-grad5" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#ff7a18" stopOpacity={1} />
                      <stop offset="100%" stopColor="#ec4899" stopOpacity={1} />
                    </linearGradient>
                    <filter id="lw-glow2">
                      <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                      <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>
                  <circle cx="140" cy="140" r="40" fill="url(#lw-grad3)" filter="url(#lw-glow2)" />
                  <text x="140" y="150" fontFamily="Arial, sans-serif" fontSize="32" fontWeight="bold" fill="white" textAnchor="middle">
                    W
                  </text>
                  <g style={{ animation: "lw-rotate 20s linear infinite", transformOrigin: "140px 140px" }}>
                    <circle cx="140" cy="140" r="80" fill="none" stroke="url(#lw-grad4)" strokeWidth="2" opacity="0.4" strokeDasharray="8,8" />
                    <circle cx="220" cy="140" r="8" fill="#06b6d4" filter="url(#lw-glow2)" />
                  </g>
                  <g style={{ animation: "lw-rotate 15s linear infinite reverse", transformOrigin: "140px 140px" }}>
                    <circle cx="140" cy="140" r="100" fill="none" stroke="url(#lw-grad5)" strokeWidth="2" opacity="0.4" strokeDasharray="6,6" />
                    <circle cx="240" cy="140" r="8" fill="#ff7a18" filter="url(#lw-glow2)" />
                  </g>
                  <g opacity="0.8">
                    <line x1="140" y1="140" x2="140" y2="60" stroke="url(#lw-grad3)" strokeWidth="3" opacity="0.3" />
                    <circle cx="140" cy="60" r="12" fill="url(#lw-grad3)" filter="url(#lw-glow2)" />
                    <line x1="140" y1="140" x2="210" y2="170" stroke="url(#lw-grad4)" strokeWidth="3" opacity="0.3" />
                    <circle cx="210" cy="170" r="12" fill="url(#lw-grad4)" filter="url(#lw-glow2)" />
                    <line x1="140" y1="140" x2="140" y2="220" stroke="url(#lw-grad5)" strokeWidth="3" opacity="0.3" />
                    <circle cx="140" cy="220" r="12" fill="url(#lw-grad5)" filter="url(#lw-glow2)" />
                    <line x1="140" y1="140" x2="70" y2="170" stroke="url(#lw-grad3)" strokeWidth="3" opacity="0.3" />
                    <circle cx="70" cy="170" r="12" fill="url(#lw-grad3)" filter="url(#lw-glow2)" />
                  </g>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Intrigued? Reach out to learn more.</h2>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="section about-section" id="about">
        <div className="container">
          <h2 className="section-title">Wendwise, brought to you by Premium Intel, LLC.</h2>
          <p className="section-subtitle">
            Founded by Premium Intel, LLC, leveraging extensive and deep expertise in the
            P&amp;C insurance industry. The Founder has over 15 years of broad experience
            from multi-national insurance companies to top tier solution providers, and has
            consulted numerous insurers on strategy and market analysis.
          </p>
        </div>
      </section>
    </div>
  );
}
